import { elevationUrl } from "./configuration";
import type { Point, Position } from "./model";

const z = 15;
export const elevation = async ([lon, lat]: Point) => {
  const world = worldCoordinates([lon, lat]);
  if (!world) return 0;
  const p = world.map(_ => _ * Math.pow(2, z));
  const [x = 0, y = 0] = p.map(_ => Math.floor(_));
  const [px = 0, py = 0] = p.map(_ => _ % 1);
  const tile = await getTile(x, y, z);
  return tile.query(px, py);
};

export const ground = async ([lon, lat]: Point | Position) => {
  const alt = await elevation([lon, lat]);
  return [lon, lat, alt] satisfies Position;
};

const worldCoordinates = ([lon, lat]: [number, number]) => {
  let siny = Math.sin((lat * Math.PI) / 180);
  siny = Math.min(Math.max(siny, -0.9999), 0.9999);
  const x = 0.5 + lon / 360;
  const y = 0.5 - Math.log((1 + siny) / (1 - siny)) / (4 * Math.PI);
  if (x < 0 || x > 1 || y < 0 || y > 1) return undefined;
  return [x, y] satisfies [number, number];
};

type Tile = {
  query(x: number, y: number): number;
};

const tileCache: { [key: string]: Promise<Tile> } = {};

const getTile = (x: number, y: number, z: number) => {
  const key = [x, y, z].join("-");
  const cached = tileCache[key];
  if (cached) return cached;
  const result = loadTile(x, y, z);
  tileCache[key] = result;
  return result;
};

const loadTile = async (x: number, y: number, z: number) => {
  const response = await fetch(
    elevationUrl
      .replace("{x}", x.toString())
      .replace("{y}", y.toString())
      .replace("{z}", z.toString()),
  );
  if (response.status === 404) return { query: () => 0 };

  const image = await createImageBitmap(await response.blob());

  const { width, height } = image;

  const canvas = new OffscreenCanvas(width, height);

  const context = canvas.getContext("2d");
  if (!context) throw new Error("Context failure");

  context.drawImage(image, 0, 0, width, height);

  const { data } = context.getImageData(0, 0, width, height);

  const query = (x: number, y: number) => {
    let index = (Math.floor(x * width) + Math.floor(y * height) * width) * 4;
    const r = data[index++] ?? 0;
    const g = data[index++] ?? 0;
    const b = data[index] ?? 0;
    return (r * 65536 + g * 256 + b) / 10 - 10000;
  };

  return { query };
};
